import { useMemo, useState } from 'react';
import { z } from 'zod';
import { Button, Input, Label, Modal } from '@shared/components';
import type { RichTextLinkPolicy } from './richTextExtensions';

const LINK_ERROR_ID = 'rich-text-link-url-error';

interface RichTextLinkDialogProps {
    initialUrl: string;
    linkPolicy: RichTextLinkPolicy;
    onSubmit: (url: string) => void;
    onRemove: () => void;
    onClose: () => void;
}

export function RichTextLinkDialog({ initialUrl, linkPolicy, onSubmit, onRemove, onClose }: RichTextLinkDialogProps) {
    const [url, setUrl] = useState(initialUrl);
    const [error, setError] = useState<string | null>(null);
    const schema = useMemo(
        () => z.string().trim().refine(linkPolicy.isAllowed, { message: linkPolicy.message }),
        [linkPolicy],
    );

    const submit = () => {
        const parsed = schema.safeParse(url);
        if (!parsed.success) {
            setError(parsed.error.issues[0].message);
            return;
        }
        onSubmit(parsed.data);
    };

    return (
        <Modal isOpen onClose={onClose} ariaLabel="Edit link" className="max-w-md p-6">
            <div className="space-y-4">
                <h3 className="text-lg font-semibold">Link</h3>
                <div>
                    <Label htmlFor="rich-text-link-url">Link address</Label>
                    <Input
                        id="rich-text-link-url"
                        value={url}
                        onChange={(event) => {
                            setUrl(event.target.value);
                            setError(null);
                        }}
                        placeholder="https://"
                        error={!!error}
                        hint={error ?? undefined}
                        hintId={LINK_ERROR_ID}
                        aria-invalid={!!error}
                        aria-describedby={error ? LINK_ERROR_ID : undefined}
                    />
                </div>
                <div className="flex justify-end gap-2">
                    {initialUrl && (
                        <Button type="button" variant="outline" onClick={onRemove}>
                            Remove link
                        </Button>
                    )}
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={submit}>
                        Apply
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
